// ============================================================
// SideQuests - shared inline SVG glyphs
//
// The single owner of every small line icon in the UI. Emoji must never
// label a control or stand in for an icon, so each icon here is a real
// vector drawn with `currentColor`, in one consistent 24x24 line style.
//
// Usage:
//   import Glyph, { glyphForNotificationType } from './Glyphs';
//   <Glyph name="check" size={16} />
//
// Icons are decorative: they carry aria-hidden and never replace a
// visible text label.
// ============================================================

import React from 'react';

export type GlyphName =
  // navigation / chrome
  | 'close'
  | 'arrow-left'
  | 'refresh'
  | 'search'
  | 'external'
  // status
  | 'check'
  | 'check-double'
  | 'check-circle'
  | 'prohibit'
  | 'circle-slash'
  | 'warning'
  | 'hourglass'
  | 'clock'
  | 'frown'
  | 'sparkles'
  // identity / people
  | 'user'
  | 'id-card'
  | 'handshake'
  | 'thumb-up'
  | 'party'
  | 'wave'
  | 'sprout'
  // quest lifecycle
  | 'rocket'
  | 'bolt'
  | 'flag'
  | 'pin'
  | 'marker'
  | 'target'
  | 'clipboard'
  | 'note'
  | 'bulb'
  | 'trash'
  | 'upload'
  | 'share'
  // money
  | 'money'
  | 'card'
  | 'lock'
  | 'receipt'
  | 'chart'
  // trust / safety
  | 'shield'
  | 'scale'
  | 'flask'
  | 'ruler'
  | 'stethoscope'
  | 'flame'
  | 'droplet'
  | 'snowflake'
  | 'ladder'
  | 'crane'
  | 'video'
  | 'hammer'
  | 'wrench'
  | 'toolbox'
  // contact / social
  | 'mail'
  | 'link'
  | 'chat'
  | 'reply'
  | 'bell'
  | 'bell-off'
  | 'megaphone'
  | 'compass'
  | 'map'
  | 'calendar'
  | 'camera'
  // rewards
  | 'star'
  | 'trophy'
  | 'medal'
  | 'rosette'
  | 'trend-up'
  | 'dot';

/** Path data per glyph. A single-entry glyph is the common case. */
const GLYPH_PATHS: Record<GlyphName, string[]> = {
  // ----- navigation / chrome -----
  close: ['M6 6l12 12', 'M18 6 6 18'],
  'arrow-left': ['M19 12H5', 'm12 19-7-7 7-7'],
  refresh: [
    'M4 11a8 8 0 0 1 13.7-5.6L21 8',
    'M21 3v5h-5',
    'M20 13a8 8 0 0 1-13.7 5.6L3 16',
    'M3 21v-5h5',
  ],
  search: ['M10.5 3a7.5 7.5 0 1 0 0 15 7.5 7.5 0 0 0 0-15Z', 'm16 16 5 5'],
  external: [
    'M14 4h6v6',
    'M20 4 11 13',
    'M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5',
  ],

  // ----- status -----
  check: ['m5 13 4 4L19 7'],
  'check-double': ['m2 13 4 4L14 9', 'm11 17 2 2L22 8'],
  'check-circle': [
    'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z',
    'm8 12.3 2.6 2.6L16 9.6',
  ],
  prohibit: [
    'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z',
    'm5.6 5.6 12.8 12.8',
  ],
  'circle-slash': ['M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z', 'M7.5 12h9'],
  warning: ['M12 4 2.5 20h19L12 4Z', 'M12 10v4', 'M12 17.5h.01'],
  hourglass: [
    'M7 3h10',
    'M7 21h10',
    'M8 3v3.2c0 1.2.5 2.3 1.5 3.1L12 12l2.5-2.7c1-.8 1.5-1.9 1.5-3.1V3',
    'M8 21v-3.2c0-1.2.5-2.3 1.5-3.1L12 12l2.5 2.7c1 .8 1.5 1.9 1.5 3.1V21',
  ],
  clock: ['M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z', 'M12 7.5V12l3 1.8'],
  frown: [
    'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z',
    'M9 10h.01',
    'M15 10h.01',
    'M8.5 16c1-1.2 2.1-1.8 3.5-1.8s2.5.6 3.5 1.8',
  ],
  sparkles: [
    'M11 3.5 12.5 8 17 9.5 12.5 11 11 15.5 9.5 11 5 9.5 9.5 8 11 3.5Z',
    'M18 14.5 18.8 16.7 21 17.5 18.8 18.3 18 20.5 17.2 18.3 15 17.5 17.2 16.7 18 14.5Z',
  ],

  // ----- identity / people -----
  user: [
    'M12 3.5a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z',
    'M4.5 20.5c0-3.6 3.4-6 7.5-6s7.5 2.4 7.5 6',
  ],
  'id-card': [
    'M3 5h18a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z',
    'M8.5 10.2a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z',
    'M5.4 16.6c.6-1.2 1.8-1.9 3.1-1.9s2.5.7 3.1 1.9',
    'M14 9.5h4',
    'M14 12.5h4',
    'M14 15.5h2',
  ],
  handshake: [
    'M8 12 5 9l3-3 2.5 2.5',
    'm16 6 3 3-4 4',
    'm10.5 8.5 3.5 3.5-3 3',
    'M3 12h2',
    'M19 12h2',
  ],
  'thumb-up': [
    'M7 11v9H4a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1h3Z',
    'M7 11l3.5-7a2 2 0 0 1 3.8 1.2L13.5 9H19a2 2 0 0 1 2 2.4l-1.2 6A2 2 0 0 1 17.8 19H7',
  ],
  party: [
    'M5 19.5 10 5l9 9-14 5.5Z',
    'M14.5 5.5 16 4',
    'M17.5 8.5 19 7',
    'M11 8.5 12.5 7',
    'M15.5 12.5 17 11',
  ],
  wave: [
    'M8 20V9a2 2 0 1 1 4 0v4',
    'M12 13V8a2 2 0 1 1 4 0v5',
    'M16 13v-2a2 2 0 1 1 4 0v4a6 6 0 0 1-6 6h-1a7 7 0 0 1-5.6-2.8L4 14.5a2 2 0 0 1 3-2.6L8 13',
  ],
  sprout: [
    'M12 21v-7',
    'M12 14c0-3-2-5-5-5 0 3 2 5 5 5Z',
    'M12 14c0-3 2-5 5-5 0 3-2 5-5 5Z',
  ],

  // ----- quest lifecycle -----
  rocket: [
    'M12 2c3 2 5 6 5 10l-3 3H10l-3-3c0-4 2-8 5-10Z',
    'M9.5 15 6 18',
    'M14.5 15 18 18',
    'M12 9h.01',
  ],
  bolt: ['M13 2 4 14h6l-1 8 9-12h-6l1-8Z'],
  flag: ['M5 3v18', 'M5 4h11l-1.6 3.5L16 11H5'],
  pin: ['M12 17v5', 'M8 3h8l-1 6 3 3H6l3-3-1-6Z'],
  marker: [
    'M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11Z',
    'M12 7.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Z',
  ],
  target: [
    'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z',
    'M12 7.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9Z',
    'M12 11a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z',
  ],
  clipboard: [
    'M9 4h6a1 1 0 0 1 1 1v1H8V5a1 1 0 0 1 1-1Z',
    'M8 6H6a1 1 0 0 0-1 1v13a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1h-2',
    'M9 11.5h6',
    'M9 15.5h4',
  ],
  note: [
    'M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z',
    'M14 3v5h5',
    'M9.5 13h5',
    'M9.5 16.5h3',
  ],
  bulb: [
    'M9.5 17.5h5',
    'M10.5 20.5h3',
    'M12 3a6 6 0 0 0-3.6 10.8c.6.5 1.1 1.2 1.1 2V17h5v-1.2c0-.8.5-1.5 1.1-2A6 6 0 0 0 12 3Z',
  ],
  trash: [
    'M4 7h16',
    'M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2',
    'M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13',
    'M10 11v6',
    'M14 11v6',
  ],
  upload: [
    'M12 16V4',
    'm7 9 5-5 5 5',
    'M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2',
  ],
  share: [
    'M18 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z',
    'M6 14.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z',
    'M18 21a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z',
    'm8.2 11.3 7.6-4.4',
    'm8.2 12.7 7.6 4.4',
  ],
// ----- money -----
  money: [
    'M12 2v20',
    'M17 6.5c0-1.9-2.2-3-5-3s-5 1.1-5 3 2.2 3 5 3 5 1.1 5 3-2.2 3-5 3-5-1.1-5-3',
  ],
  card: [
    'M3 5h18a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z',
    'M2 10h20',
    'M6 15h4',
  ],
  lock: [
    'M6 11h12a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1Z',
    'M8.5 11V7.5a3.5 3.5 0 0 1 7 0V11',
  ],
  receipt: [
    'M6 3h12v18l-3-2-3 2-3-2-3 2V3Z',
    'M9.5 8h5',
    'M9.5 12h5',
    'M9.5 16h3',
  ],
  chart: [
    'M4 20V4',
    'M4 20h16',
    'M8 17v-5',
    'M12.5 17V8',
    'M17 17v-7',
  ],

  // ----- trust / safety -----
  shield: [
    'M12 3l7 3v5.5c0 4.4-2.9 8.2-7 9.5-4.1-1.3-7-5.1-7-9.5V6l7-3Z',
  ],
  scale: [
    'M12 4v16',
    'M7 20h10',
    'M4 8h16',
    'M4 8l-2.5 5a3 3 0 0 0 5 0L4 8Z',
    'M20 8l-2.5 5a3 3 0 0 0 5 0L20 8Z',
  ],
  flask: [
    'M9 3h6',
    'M10 3v6.2L5.6 17a2 2 0 0 0 1.7 3h9.4a2 2 0 0 0 1.7-3L14 9.2V3',
    'M7.5 14h9',
  ],
  ruler: [
    'M4 14.5 14.5 4l5.5 5.5L9.5 20 4 14.5Z',
    'M8 10.5l1.8 1.8',
    'M10.8 7.7l1.8 1.8',
    'M13.6 4.9l1.8 1.8',
  ],
  stethoscope: [
    'M6 3v5a4 4 0 0 0 8 0V3',
    'M6 3H4.5',
    'M14 3h1.5',
    'M10 12v3a4 4 0 0 0 8 0v-2',
    'M18 11a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z',
  ],
  flame: [
    'M12 3c3 3.5 5 5.8 5 9a5 5 0 0 1-10 0c0-1.6.7-2.9 1.6-3.9',
    'M12 21a3 3 0 0 0 3-3c0-1.6-1.2-2.6-3-4.5-1.8 1.9-3 2.9-3 4.5a3 3 0 0 0 3 3Z',
  ],
  droplet: [
    'M12 21a6 6 0 0 0 6-6c0-4-6-12-6-12S6 11 6 15a6 6 0 0 0 6 6Z',
  ],
  snowflake: [
    'M12 3v18',
    'M4.2 7.5 19.8 16.5',
    'M19.8 7.5 4.2 16.5',
    'M12 3l-2 2',
    'M12 3l2 2',
    'M12 21l-2-2',
    'M12 21l2-2',
  ],
  ladder: [
    'M8 3v18',
    'M16 3v18',
    'M8 8h8',
    'M8 13h8',
    'M8 18h8',
  ],
  crane: [
    'M4 21V5',
    'M4 5h13',
    'M4 9h7',
    'M13 5v5',
    'M13 10h4l-2 3h-2l-2-3Z',
    'M9 21h10',
  ],
  video: [
    'M3 6h12a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Z',
    'M16 10.5 21 8v8l-5-2.5',
  ],
  hammer: [
    'M14 4l6 6-2.5 2.5-6-6L14 4Z',
    'M11.5 6.5 4 14v4h4l7.5-7.5',
  ],
  wrench: [
    'M15.5 3a5.5 5.5 0 0 0-4.7 8.4L4 18.2 5.8 20l6.8-6.8A5.5 5.5 0 1 0 15.5 3Z',
  ],
  toolbox: [
    'M3 8h18a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z',
    'M9 8V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3',
    'M2 12.5h20',
    'M10.5 12.5h3',
  ],

  // ----- contact / social -----
  mail: [
    'M3 6h18a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Z',
    'm3.6 6.6 8.4 6 8.4-6',
  ],
  link: [
    'M10 13.5a4 4 0 0 0 5.7 0l2.6-2.6a4 4 0 0 0-5.7-5.7l-1 1',
    'M14 10.5a4 4 0 0 0-5.7 0l-2.6 2.6a4 4 0 0 0 5.7 5.7l1-1',
  ],
  chat: [
    'M21 11.5a8.4 8.4 0 0 1-9 8.4 9.9 9.9 0 0 1-3.3-.6L4 21l1.7-4.2A8.4 8.4 0 0 1 12 3a8.4 8.4 0 0 1 9 8.5Z',
  ],
  reply: [
    'M9 17H7a4 4 0 0 1 0-8h10',
    'm14 6 3 3-3 3',
  ],
  bell: [
    'M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9',
    'M13.7 21a2 2 0 0 1-3.4 0',
  ],
  'bell-off': [
    'M8.7 3A6 6 0 0 1 18 8a21 21 0 0 0 .6 5',
    'M17 17H3s3-2 3-9a6 6 0 0 1 .5-2.4',
    'M10.3 21a2 2 0 0 0 3.4 0',
    'm2 2 20 20',
  ],
  megaphone: [
    'M3 11v2a1 1 0 0 0 1 1h3l7 4V6l-7 4H4a1 1 0 0 0-1 1Z',
    'M14 6v12',
    'M18.5 9.5a4 4 0 0 1 0 5',
  ],
  compass: [
    'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z',
    'm15.5 8.5-2 5-5 2 2-5 5-2Z',
  ],
  map: [
    'M9 4 3.6 6.2A1 1 0 0 0 3 7.1v12.4a1 1 0 0 0 1.4.9L9 18l6 2 5.4-2.2a1 1 0 0 0 .6-.9V6.5a1 1 0 0 0-1.4-.9L15 8 9 6V4Z',
    'M9 4v14',
    'M15 8v12',
  ],
  calendar: [
    'M4 6h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Z',
    'M3 11h18',
    'M8 3v3',
    'M16 3v3',
  ],
  camera: [
    'M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z',
    'M12 9.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7Z',
  ],

  // ----- rewards -----
  star: [
    'M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.5 9.7l5.9-.9L12 3.5Z',
  ],
  trophy: [
    'M8 4h8v5a4 4 0 0 1-8 0V4Z',
    'M8 5.5H5.5a2.5 2.5 0 0 0 2.5 2.5',
    'M16 5.5h2.5a2.5 2.5 0 0 1-2.5 2.5',
    'M12 13v4',
    'M8.5 20.5h7',
    'M10 17h4l.5 3.5h-5L10 17Z',
  ],
  medal: [
    'M12 9a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11Z',
    'm8.5 9.5 -3-6.5',
    'm15.5 9.5 3-6.5',
    'M12 11.5l.9 1.8 2 .3-1.4 1.4.3 2-1.8-1-1.8 1 .3-2-1.4-1.4 2-.3.9-1.8Z',
  ],
  rosette: [
    'M12 3a5 5 0 1 0 0 10 5 5 0 0 0 0-10Z',
    'm9 12.5 -2 8.5 5-2.5 5 2.5-2-8.5',
  ],
  'trend-up': [
    'M3 17 9.5 10.5l4 4L21 7',
    'M15 7h6v6',
  ],
  dot: ['M12 6a6 6 0 1 1 0 12 6 6 0 0 1 0-12Z'],
};
/** Glyphs that read better filled than stroked. */
const FILLED = new Set<GlyphName>(['dot']);

export interface GlyphProps {
  name: GlyphName;
  /** Rendered square size in pixels. */
  size?: number;
  strokeWidth?: number;
  className?: string;
}

export default function Glyph({ name, size = 20, strokeWidth = 2, className }: GlyphProps) {
  const filled = FILLED.has(name);
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke={filled ? 'none' : 'currentColor'}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {GLYPH_PATHS[name].map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}

/**
 * The icon for a notification type. Single source of truth, shared by the
 * notification centre and the inbox so the two can never drift apart.
 */
export function glyphForNotificationType(type: string): GlyphName {
  switch (type) {
    case 'quest_claimed':
      return 'handshake';
    case 'quest_completed':
      return 'check-circle';
    case 'quest_completed_approval':
      return 'hourglass';
    case 'payment_released':
      return 'money';
    case 'payment_disputed':
      return 'prohibit';
    case 'chat':
      return 'chat';
    case 'boost':
      return 'rocket';
    case 'xp':
      return 'bolt';
    case 'level_up':
      return 'trend-up';
    case 'approval':
      return 'thumb-up';
    case 'payout':
      return 'receipt';
    default:
      return 'bell';
  }
}
